import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import type { BlogCategory } from '../../types/blog';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../constants/blog';
import { CarouselArrowButton } from '../ui/CarouselArrowButton';

/**
 * CategoryPillNav — shared sibling-navigation chrome for the blog.
 *
 * Renders the same horizontal row of category labels on /blog and /blog/:category,
 * with the active pill highlighted via a brand-primary underline.
 *
 * Semantics: this is real navigation (each pill is a react-router Link to a
 * different route), NOT a tablist. ARIA roles reflect that — <nav> with
 * aria-label, and aria-current="page" on the active link.
 *
 * Below lg the strip is an ARROWS-ONLY pager: the container is overflow-hidden
 * (no free swipe/scroll, no scrollbar) and only the flanking ‹ › arrows move it,
 * paging by the visible count (2 on mobile, 3 on tablet). The active category is
 * always scrolled into view on mount/navigation (so selecting a pill on the last
 * page doesn't reload to the first page). At lg+ all pills fit → no arrows.
 *
 * Every pill reserves the *active* (bold) width via an invisible ghost span, so
 * switching category never reflows the strip (no width jump on desktop).
 *
 * Category labels and ordering live in src/constants/blog.ts (single source of
 * truth — also consumed by BlogCategoryPage H1 and BlogPostPage breadcrumb).
 * The "Latest" pill is rendered separately below because it is not a
 * BlogCategory — it is the /blog index route.
 */
interface CategoryPillNavProps {
    activeCategory: BlogCategory | 'all';
}

// Pill items derived from the shared constants. The "Latest" / 'all' pill is
// prepended here because the /blog index route is not a BlogCategory.
const NAV_ITEMS: { value: BlogCategory | 'all'; label: string; href: string }[] = [
    { value: 'all', label: 'Latest', href: '/blog' },
    ...CATEGORY_ORDER.map((category) => ({
        value: category,
        label: CATEGORY_LABELS[category],
        href: `/blog/${category}`,
    })),
];

const CategoryPillNav: React.FC<CategoryPillNavProps> = ({ activeCategory }) => {
    const scrollRef = useRef<HTMLElement>(null);
    const activeIndex = Math.max(0, NAV_ITEMS.findIndex((i) => i.value === activeCategory));

    // perPage = how many pills are visible below lg (2 mobile / 3 tablet). At lg+
    // all pills fit → no paging, no arrows. SSR defaults to desktop (the SSG posture)
    // so the server markup carries no arrows; mobile/tablet add them on mount.
    const [isDesktop, setIsDesktop] = useState(true);
    const [perPage, setPerPage] = useState(3);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const recalc = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanPrev(el.scrollLeft > 1);
        setCanNext(el.scrollLeft < max - 1);
    }, []);

    // Scroll so the pill at `index` becomes the left-most visible one (a page start).
    // overflow-hidden blocks user scrolling but programmatic scrollTo still works.
    const scrollToIndex = useCallback((index: number, smooth = true) => {
        const el = scrollRef.current;
        if (!el) return;
        const pills = el.querySelectorAll<HTMLElement>('[data-pill]');
        const first = pills[0];
        const target = pills[Math.min(Math.max(index, 0), pills.length - 1)];
        if (!first || !target) return;
        const reduce = typeof window !== 'undefined'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    }, []);

    useEffect(() => {
        const compute = () => {
            const w = window.innerWidth;
            setIsDesktop(w >= 1024);
            setPerPage(w >= 768 ? 3 : 2);
        };
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, []);

    // Keep the active category in view: on mount / active or perPage change, jump
    // (no animation) to the page that contains the active pill.
    useEffect(() => {
        if (isDesktop) return;
        const pageStart = Math.floor(activeIndex / perPage) * perPage;
        scrollToIndex(pageStart, false);
        recalc();
    }, [activeIndex, perPage, isDesktop, scrollToIndex, recalc]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', recalc, { passive: true });
        window.addEventListener('resize', recalc);
        recalc();
        return () => {
            el.removeEventListener('scroll', recalc);
            window.removeEventListener('resize', recalc);
        };
    }, [recalc]);

    // Page by `perPage` pills, relative to the current left-most visible pill.
    const pageBy = useCallback((dir: -1 | 1) => {
        const el = scrollRef.current;
        if (!el) return;
        const pills = el.querySelectorAll<HTMLElement>('[data-pill]');
        const first = pills[0];
        if (!first) return;
        let cur = 0;
        let best = Infinity;
        pills.forEach((p, i) => {
            const d = Math.abs((p.offsetLeft - first.offsetLeft) - el.scrollLeft);
            if (d < best) { best = d; cur = i; }
        });
        scrollToIndex(cur + dir * perPage, true);
    }, [perPage, scrollToIndex]);

    const showArrows = !isDesktop && NAV_ITEMS.length > perPage;

    return (
        <div className="flex items-center gap-1 mb-8">
            {showArrows && (
                <CarouselArrowButton
                    direction="prev"
                    onClick={() => pageBy(-1)}
                    disabled={!canPrev}
                    aria-label="Scroll categories left"
                    className="flex-shrink-0 self-end mb-[1px]"
                />
            )}
            {/* overflow-x-hidden disables user swipe/scroll (and the scrollbar) — the strip
                only moves via the arrows, which drive a programmatic scrollTo. */}
            <nav
                ref={scrollRef}
                aria-label="Blog category navigation"
                className="overflow-x-hidden overflow-y-hidden flex-1 min-w-0"
            >
                {/* Below lg: fixed columns — 2 visible on mobile (calc 50%), 3 on tablet
                    (calc 33.3%). At lg: content-sized grid (w-max) with equal auto columns so
                    every tab equals the widest label's width and all pills fit (no arrows). */}
                <div className="grid grid-flow-col auto-cols-[calc(50%-0.375rem)] md:auto-cols-[calc(33.333%-0.5rem)] gap-3 pt-3 lg:auto-cols-fr lg:w-max">
                    {NAV_ITEMS.map((item) => {
                        const isActive = activeCategory === item.value;
                        return (
                            <Link
                                key={item.value}
                                data-pill=""
                                to={item.href}
                                aria-current={isActive ? 'page' : undefined}
                                className={cn(
                                    'relative flex items-center justify-center whitespace-nowrap px-[1ch] pb-3 border-b-2 text-sm transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-primary',
                                    isActive ? 'border-brand-primary' : 'border-transparent',
                                )}
                            >
                                {/* Invisible bold ghost reserves the active (bold) width for EVERY
                                    pill, so switching category never widens/reflows the strip. */}
                                <span aria-hidden="true" className="invisible font-semibold">{item.label}</span>
                                <span
                                    className={cn(
                                        'absolute inset-0 flex items-center justify-center transition-colors',
                                        isActive
                                            ? 'text-text-primary font-semibold'
                                            : 'text-text-muted hover:text-text-primary font-medium',
                                    )}
                                >
                                    {item.label}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </nav>
            {showArrows && (
                <CarouselArrowButton
                    direction="next"
                    onClick={() => pageBy(1)}
                    disabled={!canNext}
                    aria-label="Scroll categories right"
                    className="flex-shrink-0 self-end mb-[1px]"
                />
            )}
        </div>
    );
};

export default CategoryPillNav;
